import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d6tkv8q4of3kuoicn7dg';

const allLocations = [
  { id: 'minneapolis', city: 'Minneapolis', state: 'MN', county: 'Hennepin', lat: '44.9778', lng: '-93.2650', mapId: 'ChIJpTvG15DL1IkRd8S0KlBVNTI' },
  { id: 'st-paul', city: 'St. Paul', state: 'MN', county: 'Ramsey', lat: '44.9537', lng: '-93.0900', mapId: 'ChIJtfReSB8s9ocRpKSFlT11YxE' },
  { id: 'bloomington', city: 'Bloomington', state: 'MN', county: 'Hennepin', lat: '44.8408', lng: '-93.3439', mapId: '' },
  { id: 'plymouth', city: 'Plymouth', state: 'MN', county: 'Hennepin', lat: '45.0105', lng: '-93.4555', mapId: '' },
  { id: 'maple-grove', city: 'Maple Grove', state: 'MN', county: 'Hennepin', lat: '45.0724', lng: '-93.4557', mapId: '' },
  { id: 'eagan', city: 'Eagan', state: 'MN', county: 'Dakota', lat: '44.8041', lng: '-93.1669', mapId: '' },
  { id: 'eden-prairie', city: 'Eden Prairie', state: 'MN', county: 'Hennepin', lat: '44.8547', lng: '-93.4708', mapId: '' },
  { id: 'coon-rapids', city: 'Coon Rapids', state: 'MN', county: 'Anoka', lat: '45.1197', lng: '-93.3108', mapId: '' },
  { id: 'brooklyn-park', city: 'Brooklyn Park', state: 'MN', county: 'Hennepin', lat: '45.0941', lng: '-93.3737', mapId: '' },
  { id: 'woodbury', city: 'Woodbury', state: 'MN', county: 'Washington', lat: '44.9239', lng: '-92.9594', mapId: '' },
];

const getLocationData = (id: string) => {
  return allLocations.find((l) => l.id === id) || allLocations[0];
};

const services = [
  { icon: 'ri-map-pin-2-line', title: 'Local Moving', id: 'local-moving' },
  { icon: 'ri-home-4-line', title: 'Residential Moving', id: 'residential-moving' },
  { icon: 'ri-building-2-line', title: 'Apartment Moving', id: 'apartment-moving' },
  { icon: 'ri-store-2-line', title: 'Commercial Moving', id: 'commercial-moving' },
  { icon: 'ri-service-line', title: 'Full-Service Moving', id: 'full-service-moving' },
  { icon: 'ri-shield-check-line', title: 'Heavy-Item Moving', id: 'heavy-item-moving' },
];

const useVisible = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
};

const LocationContent = ({ id }: { id: string }) => {
  const loc = getLocationData(id);
  const [visible, setVisible] = useState(false);
  const [formState, setFormState] = useState({ name: '', phone: '', email: '', city: loc.city, message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const s1 = useVisible();
  const s2 = useVisible();
  const s3 = useVisible();

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
  }, [id]);

  const faqs = [
    { q: `Do you provide moving services in ${loc.city}?`, a: `Yes. TrueCare Moving Services provides professional residential and commercial moving in ${loc.city}, MN. We\'re a licensed, insured, family-owned company serving ${loc.city} and the surrounding ${loc.county} County area. Call (612) 367-4481 to schedule your free quote or check availability.` },
    { q: `How much does moving cost in ${loc.city}, MN?`, a: `Moving costs in ${loc.city} depend on home size, distance, and services required. Most local moves in ${loc.city} range from $300 to $1,200. TrueCare provides transparent, upfront quotes with no hidden fees. Call (612) 367-4481 or fill out our form for a free estimate specific to your ${loc.city} move.` },
    { q: `How quickly can you schedule a move in ${loc.city}?`, a: `TrueCare Moving Services can often schedule ${loc.city} moves within 1–3 days for standard requests. For last-minute or urgent moves in ${loc.city}, we may be able to accommodate same-day or next-day scheduling depending on availability. Call (612) 367-4481 for the fastest response.` },
    { q: `Do you handle apartment moves in ${loc.city}?`, a: `Yes. TrueCare specializes in apartment moving in ${loc.city} including high-rise buildings, condos, and townhomes. We coordinate elevator scheduling, navigate building access requirements, and protect floors and walls throughout your ${loc.city} apartment move. Call us to get started.` },
    { q: `Are your ${loc.city} movers licensed and insured?`, a: `Yes, TrueCare Moving Services LLC is fully licensed and insured in Minnesota for all moves including ${loc.city}. We carry proper liability coverage, are A+ BBB rated, and have 231 five-star Google reviews from customers across Minneapolis and surrounding communities including ${loc.city}.` },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ ...formState });
      await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-hero-gradient overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={`https://public.readdy.ai/ai/img_res/d296d9eaf73f864e0b8635b088a77380.jpg$%7BencodeURIComponent%28loc.city%29%7D%20Minnesota%20residential%20neighborhood%2C%20sunny%20day%2C%20suburban%20street%20with%20trees%2C%20clean%20white%20truck%2C%20professional%20movers%2C%20high%20quality%20photography%2C%20warm%20tones%2C%20premium%20aesthetic%2C%20award%20winning%20commercial%20photography&width=1440&height=700&seq=loc-${id}&orientation=landscape`}
            alt={`Moving services in ${loc.city}, MN`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`max-w-3xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-teal/20 border border-teal/30 rounded-full px-4 py-2 mb-6">
              <i className="ri-map-pin-fill text-teal text-xs"></i>
              <span className="text-teal text-xs font-body tracking-widest uppercase">{loc.city}, {loc.state} Moving Services</span>
            </div>
            <h1 className="font-heading font-800 text-white text-5xl lg:text-6xl mb-5 leading-tight">
              {loc.city} Moving<br />Services You Can Trust
            </h1>
            <p className="text-white/70 font-body text-xl leading-relaxed mb-8 max-w-xl">
              TrueCare Moving Services — Minneapolis&apos;s licensed, insured, family-owned movers proudly serving {loc.city} and surrounding {loc.county} County communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a href="#quote" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-file-text-line"></i>Get Free Quote
              </a>
              <a href="tel:+16123674481" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-heading font-600 px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-teal"></i>Call (612) 367-4481
              </a>
            </div>
            <div className="flex flex-wrap gap-4">
              {['Licensed & Insured', 'A+ BBB Rated', 'No Hidden Fees', '5-Star Reviews'].map((b) => (
                <div key={b} className="flex items-center gap-1.5">
                  <i className="ri-check-line text-teal text-sm"></i>
                  <span className="text-white/60 font-body text-sm">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services in City */}
      <section ref={s1.ref} className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-14 transition-all duration-700 ${s1.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-teal"></div>
              <span className="text-teal font-body text-xs tracking-widest uppercase">Available in {loc.city}</span>
              <div className="h-0.5 w-8 bg-teal"></div>
            </div>
            <h2 className="font-heading font-800 text-navy text-4xl mb-4">
              Moving Services in {loc.city}, MN
            </h2>
            <p className="text-charcoal/60 font-body text-lg max-w-xl mx-auto">
              TrueCare provides a complete range of professional moving services for {loc.city} residents and businesses.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Link
                key={svc.id}
                to={`/services/${svc.id}`}
                className={`group bg-white rounded-2xl p-7 border border-transparent hover:border-teal/30 transition-all duration-700 cursor-pointer hover:-translate-y-1 ${s1.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: s1.visible ? `${i * 80}ms` : '0ms' }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-navy/5 group-hover:bg-teal/15 rounded-xl mb-4 transition-colors">
                  <i className={`${svc.icon} text-navy group-hover:text-teal text-xl transition-colors`}></i>
                </div>
                <h3 className="font-heading font-700 text-navy text-base mb-2">{svc.title} in {loc.city}</h3>
                <div className="flex items-center gap-1.5 text-teal font-heading font-600 text-sm mt-3">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Quote Form */}
      <section ref={s2.ref} className="py-20 bg-white" id="quote">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className={`transition-all duration-700 ${s2.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="h-0.5 w-8 bg-teal"></div>
                <span className="text-teal font-body text-xs tracking-widest uppercase">{loc.city} Service Area</span>
              </div>
              <h2 className="font-heading font-800 text-navy text-3xl mb-4">
                Your Local {loc.city} Movers
              </h2>
              <p className="text-charcoal/65 font-body text-base leading-relaxed mb-6">
                TrueCare Moving Services is your trusted local moving company serving {loc.city} and all surrounding {loc.county} County communities. Our team is familiar with {loc.city} neighborhoods, buildings, and logistics — giving you a smoother, faster, more reliable move.
              </p>
              <div className="rounded-2xl overflow-hidden border border-navy/10" style={{ height: '350px' }}>
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d91234.25892034168!2d${parseFloat(loc.lng) - 0.2}!3d${loc.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${encodeURIComponent(loc.city + ', ' + loc.state)}!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`TrueCare Moving Services in ${loc.city}, MN`}
                ></iframe>
              </div>
              <div className="mt-4 bg-navy rounded-xl px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-teal/20 rounded-lg shrink-0">
                  <i className="ri-map-pin-line text-teal text-lg"></i>
                </div>
                <div>
                  <div className="font-heading font-700 text-white text-sm">Proudly Serving {loc.city}, {loc.state}</div>
                  <div className="text-text-muted font-body text-xs mt-0.5">{loc.county} County · Twin Cities Metro Area</div>
                </div>
              </div>
            </div>
            {/* Quote Form */}
            <div className={`bg-navy rounded-2xl p-8 transition-all duration-700 delay-200 ${s2.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h3 className="font-heading font-700 text-white text-xl mb-2">Get a Free {loc.city} Moving Quote</h3>
              <p className="text-text-muted font-body text-sm mb-6">No obligation. Fast response. No hidden fees.</p>
              {submitted ? (
                <div className="text-center py-12">
                  <i className="ri-check-double-line text-teal text-5xl mb-4 block"></i>
                  <h4 className="font-heading font-700 text-white text-lg mb-2">Quote Request Received!</h4>
                  <p className="text-text-muted font-body text-sm">We&apos;ll contact you within hours. Call (612) 367-4481 for immediate help.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Name *</label>
                      <input type="text" name="name" required value={formState.name} onChange={(e) => setFormState(p => ({ ...p, name: e.target.value }))} placeholder="Your name" className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Phone *</label>
                      <input type="tel" name="phone" required value={formState.phone} onChange={(e) => setFormState(p => ({ ...p, phone: e.target.value }))} placeholder="(612) 000-0000" className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-text-muted font-body text-xs mb-1.5">Email</label>
                    <input type="email" name="email" value={formState.email} onChange={(e) => setFormState(p => ({ ...p, email: e.target.value }))} placeholder="your@email.com" className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none" />
                  </div>
                  <input type="hidden" name="city" value={formState.city} />
                  <div>
                    <label className="block text-text-muted font-body text-xs mb-1.5">About Your Move</label>
                    <textarea name="message" maxLength={500} value={formState.message} onChange={(e) => setFormState(p => ({ ...p, message: e.target.value }))} rows={4} placeholder={`Tell us about your ${loc.city} move — size, dates, services needed...`} className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none resize-none"></textarea>
                    <div className="text-text-muted text-xs text-right mt-1">{formState.message.length}/500</div>
                  </div>
                  <button type="submit" disabled={submitting} className="w-full bg-teal hover:bg-teal-dark text-navy font-heading font-700 py-4 rounded-xl transition-all text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
                    {submitting ? 'Sending...' : `Get Free ${loc.city} Moving Quote`}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* AI Answer Block */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-white text-3xl lg:text-4xl mb-3">
              Moving in {loc.city}? Here&apos;s What You Need to Know
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: 'ri-question-line', q: `What moving services are available in ${loc.city}?`, a: `TrueCare Moving Services provides local moving, residential moving, apartment moving, commercial moving, full-service moving, heavy-item moving, last-minute moving, and loading/unloading in ${loc.city}, MN. All services are handled by our licensed, insured, experienced team at transparent rates.` },
              { icon: 'ri-user-smile-line', q: `Why choose TrueCare for moving in ${loc.city}?`, a: `TrueCare is a licensed, insured, family-owned Minneapolis company with 10+ years experience and 231 five-star Google reviews. We serve ${loc.city} with the same premium service that has made us the most trusted movers in the Twin Cities — punctual, careful, transparent, and genuinely committed to your satisfaction.` },
              { icon: 'ri-timer-flash-line', q: `Do you offer last-minute moves in ${loc.city}?`, a: `Yes. TrueCare offers rapid-response last-minute moving in ${loc.city}. We understand emergencies happen and work to accommodate same-day and next-day move requests when availability allows. Call (612) 367-4481 immediately for the fastest scheduling for your urgent ${loc.city} move.` },
              { icon: 'ri-map-pin-line', q: `What areas near ${loc.city} does TrueCare serve?`, a: `In addition to ${loc.city}, TrueCare serves Minneapolis, St. Paul, and 20+ Twin Cities communities including Bloomington, Plymouth, Maple Grove, Eagan, Eden Prairie, Edina, Burnsville, Lakeville, Coon Rapids, Brooklyn Park, Minnetonka, St. Louis Park, and more throughout the metro area.` },
            ].map((block, i) => (
              <div key={i} className="bg-white/5 border border-white/10 hover:border-teal/30 rounded-2xl p-7 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal/15 rounded-xl shrink-0">
                    <i className={`${block.icon} text-teal text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-700 text-white text-sm mb-2">{block.q}</h3>
                    <p className="text-text-muted font-body text-sm leading-relaxed">{block.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local FAQ */}
      <section ref={s3.ref} className="py-20 bg-white" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-700 ${s3.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading font-800 text-navy text-3xl lg:text-4xl mb-3">
              {loc.city} Moving FAQs
            </h2>
            <p className="text-charcoal/60 font-body text-base">Common questions about moving to and from {loc.city}, MN.</p>
          </div>
          <div className={`space-y-3 transition-all duration-700 delay-200 ${s3.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {faqs.map((faq, i) => (
              <div key={i} className={`border rounded-xl overflow-hidden transition-all ${openFaq === i ? 'border-teal/30 bg-teal/5' : 'border-navy/10 bg-cream'}`} itemScope itemType="https://schema.org/Question">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer">
                  <span className="font-heading font-600 text-navy text-sm pr-4" itemProp="name">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 transition-all ${openFaq === i ? 'bg-teal text-navy' : 'bg-navy/5 text-navy/50'}`}>
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-sm`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5" itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                    <p className="text-charcoal/65 font-body text-sm leading-relaxed" itemProp="text">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-navy text-center">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-800 text-white text-4xl mb-4">Moving in {loc.city}?<br />Let TrueCare Handle It.</h2>
          <p className="text-text-muted font-body text-lg mb-8">Licensed. Insured. Family-owned. Trusted by {loc.city} families for 10+ years.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="#quote" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-10 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-file-text-line"></i>Get Free Quote
            </a>
            <a href="tel:+16123674481" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-teal text-white hover:text-teal font-heading font-600 px-10 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i>Call (612) 367-4481
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const LocationsPage = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return (
      <div style={{ fontFamily: '"Inter", sans-serif' }}>
        <Header />
        <main>
          <section className="pt-32 pb-20 bg-hero-gradient">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
              <h1 className="font-heading font-800 text-white text-5xl mb-5">Twin Cities Service Areas</h1>
              <p className="text-white/70 font-body text-xl max-w-2xl mx-auto mb-10">
                TrueCare Moving Services proudly serves Minneapolis and 20+ surrounding Twin Cities communities.
              </p>
              <div className="flex justify-center gap-4 mb-16">
                <Link to="/#quote" className="bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">Get Free Quote</Link>
                <a href="tel:+16123674481" className="bg-white/10 border border-white/30 text-white font-heading font-600 px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">Call (612) 367-4481</a>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
                {allLocations.map((loc) => (
                  <Link key={loc.id} to={`/locations/${loc.id}`} className="bg-white/10 hover:bg-teal/20 border border-white/20 hover:border-teal/40 text-white hover:text-teal rounded-xl px-4 py-4 text-sm font-heading font-600 transition-all cursor-pointer flex items-center gap-2 justify-center">
                    <i className="ri-map-pin-line text-teal text-sm shrink-0"></i>
                    {loc.city}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: '"Inter", sans-serif' }}>
      <Header />
      <main>
        <LocationContent id={id} />
      </main>
      <Footer />
    </div>
  );
};

export default LocationsPage;
